import { z } from 'zod';
import {
  APPOINTMENT_STATUSES,
  BUSINESS_TYPES,
  COMPANY_USER_ROLES,
  COUPON_TYPES,
  FINANCE_ENTRY_STATUSES,
  FINANCE_ENTRY_TYPES,
  FINANCE_RECURRENCES,
  MAX_INSTALLMENTS,
  MESSAGE_EVENTS,
  PAGINATION,
  PAYMENT_METHODS,
  PERMISSION_LEVELS,
  PERMISSION_MODULES,
  PROFILE_KEYS,
  RECEIVABLE_STATUSES,
  UNIT_TYPES,
  USER_ROLES,
} from '../constants/index';

// ---------------------------------------------------------------------------
// Primitivos reutilizaveis
// ---------------------------------------------------------------------------

export const emailSchema = z.string().email('E-mail invalido');

export const passwordSchema = z
  .string()
  .min(8, 'A senha deve ter no minimo 8 caracteres')
  .max(128, 'A senha deve ter no maximo 128 caracteres');

export const idSchema = z.string().min(1, 'ID obrigatorio');

export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(PAGINATION.DEFAULT_PAGE),
  perPage: z.coerce
    .number()
    .int()
    .positive()
    .max(PAGINATION.MAX_PER_PAGE)
    .default(PAGINATION.DEFAULT_PER_PAGE),
  search: z.string().trim().optional(),
});

export const paginationMetaSchema = z.object({
  total: z.number().int(),
  page: z.number().int(),
  perPage: z.number().int(),
  totalPages: z.number().int(),
});

// ---------------------------------------------------------------------------
// Enums de dominio
// ---------------------------------------------------------------------------

export const userRoleSchema = z.enum(USER_ROLES);

/**
 * O papel que um usuario de salao pode ter. O papel do SaaS fica de fora de
 * proposito: sem isso, um administrador de salao se promoveria a administrador
 * do sistema pelo proprio formulario de usuarios.
 */
export const companyUserRoleSchema = z.enum(COMPANY_USER_ROLES);
export const profileKeySchema = z.enum(PROFILE_KEYS);
export const permissionModuleSchema = z.enum(PERMISSION_MODULES);
export const permissionLevelSchema = z.enum(PERMISSION_LEVELS);

// ---------------------------------------------------------------------------
// Usuarios, grupos e senha (modulo 1 do roadmap)
// ---------------------------------------------------------------------------

const nameSchema = z.string().trim().min(2, 'Nome muito curto').max(80);

/**
 * Foto: um endereco publico ou a propria imagem embutida (data URL), que e
 * como a tela envia o arquivo escolhido pelo usuario - o sistema nao tem
 * servico de arquivos, entao a imagem vai reduzida dentro do proprio campo.
 * String vazia limpa a foto.
 */
const MAX_DATA_URL = 400_000;

export const imageUrlSchema = z.union([
  z
    .string()
    .startsWith('data:image/', 'Formato de imagem invalido')
    .max(MAX_DATA_URL, 'Imagem muito grande'),
  z.string().url('URL invalida').max(2048),
  z.literal(''),
]);

export const createUserSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  password: passwordSchema,
  role: companyUserRoleSchema.default('MEMBER'),
  isActive: z.boolean().default(true),
  /** Perfis de acesso do usuario - e daqui que vem toda a permissao dele. */
  profileIds: z.array(idSchema).default([]),
});

/** Handle ou endereco da rede; string vazia limpa o campo. */
const socialSchema = z.string().trim().max(120);

export const updateUserSchema = z
  .object({
    name: nameSchema,
    role: companyUserRoleSchema,
    isActive: z.boolean(),
    profileIds: z.array(idSchema),
    // Os mesmos dados pessoais do proprio perfil: quem administra tambem
    // corrige o telefone e a foto de quem trabalha no salao.
    image: imageUrlSchema.nullable(),
    phone: z.string().trim().max(40),
    gender: z.enum(['MALE', 'FEMALE', 'UNDISCLOSED']).nullable(),
    instagram: socialSchema,
    facebook: socialSchema,
    x: socialSchema,
  })
  .partial();

export const updateProfileSchema = z
  .object({
    name: nameSchema,
    image: imageUrlSchema.nullable(),
    phone: z.string().trim().max(40),
    gender: z.enum(['MALE', 'FEMALE', 'UNDISCLOSED']).nullable(),
    instagram: socialSchema,
    facebook: socialSchema,
    x: socialSchema,
  })
  .partial();

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Informe a senha atual'),
  newPassword: passwordSchema,
});

export const forgotPasswordSchema = z.object({
  email: emailSchema,
});

export const resetPasswordSchema = z.object({
  token: z.string().min(1, 'Token obrigatorio'),
  password: passwordSchema,
});

export const groupInputSchema = z.object({
  name: nameSchema,
  description: z.string().trim().max(240).optional(),
  profiles: z.array(profileKeySchema).default([]),
});

export const updateGroupSchema = groupInputSchema.partial();

// ---------------------------------------------------------------------------
// Entidades
// ---------------------------------------------------------------------------

export const businessTypeSchema = z.enum(BUSINESS_TYPES);
export const unitTypeSchema = z.enum(UNIT_TYPES);

/** Percentual de desconto: inteiro de 0 a 100. */
const percentSchema = z.coerce.number().int().min(0).max(100);

export const companyInputSchema = z.object({
  name: z.string().trim().min(2, 'Nome muito curto').max(120),
  legalName: z.string().trim().max(160).optional(),
  document: z.string().trim().max(20).regex(/^\d*$/, 'Informe apenas numeros').optional(),
  businessType: businessTypeSchema,
  city: z.string().trim().min(1, 'Informe a cidade').max(120).optional(),
  state: z.string().trim().min(1, 'Informe o estado').max(60).optional(),
  logo: imageUrlSchema.optional(),
  phone: z.string().trim().max(40).optional(),
  /**
   * Minutos de atraso ate o horario virar desistencia sozinho. Zero ou vazio
   * desliga: o salao fecha na mao.
   */
  noShowAfterMinutes: z.coerce
    .number()
    .int()
    .min(0)
    .max(24 * 60)
    .optional(),
  /** Metas mensais; zero significa "sem meta" e some do painel. */
  revenueGoal: z.coerce.number().min(0).max(10_000_000).optional(),
  customersGoal: z.coerce.number().int().min(0).max(100_000).optional(),
  servicesGoal: z.coerce.number().int().min(0).max(100_000).optional(),
  /** Clientes de casa que devem voltar no mes. */
  returningGoal: z.coerce.number().int().min(0).max(100_000).optional(),
  /** Crescimento de vendas sobre o mes anterior, em porcentagem. */
  winRateGoal: z.coerce.number().min(0).max(100).optional(),
  /** Numeros de dinheiro visiveis so para o administrador. */
  revenueGoalAdminOnly: z.boolean().optional(),
  /** Subconta desta empresa na Twilio (AC...); vazio usa a conta do SaaS. */
  twilioSubaccountSid: z.string().trim().max(64).optional(),
});

export const updateCompanySchema = companyInputSchema.partial();

export const unitInputSchema = z.object({
  name: z.string().trim().min(2, 'Nome muito curto').max(120),
  type: unitTypeSchema.default('BRANCH'),
  phone: z.string().trim().max(40).optional(),
  city: z.string().trim().max(80).optional(),
  address: z.string().trim().max(200).optional(),
  isActive: z.boolean().default(true),
});

export const updateUnitSchema = unitInputSchema.partial();

export const customerInputSchema = z.object({
  name: z.string().trim().min(2, 'Nome muito curto').max(120),
  /** Foto do cliente; string vazia limpa. */
  image: imageUrlSchema.optional(),
  email: z.union([emailSchema, z.literal('')]).optional(),
  phone: z.string().trim().max(40).optional(),
  document: z.string().trim().max(20).optional(),
  birthDate: z.coerce.date().optional(),
  gender: z.enum(['MALE', 'FEMALE', 'UNDISCLOSED']).optional(),
  /** So o ano: e o que o cadastro rapido pede, sem a data toda. */
  birthYear: z.coerce.number().int().min(1900).max(new Date().getFullYear()).optional(),
  notes: z.string().trim().max(1000).optional(),
  /** Vazio = cliente da empresa toda, sem unidade preferencial. */
  unitId: z.string().optional(),
  isFavorite: z.boolean().default(false),
  isActive: z.boolean().default(true),
});

export const updateCustomerSchema = customerInputSchema.partial();

export const categoryInputSchema = z.object({
  name: z.string().trim().min(2, 'Nome muito curto').max(80),
  description: z.string().trim().max(240).optional(),
  image: imageUrlSchema.optional(),
  isActive: z.boolean().default(true),
});

export const updateCategorySchema = categoryInputSchema.partial();

export const serviceInputSchema = z.object({
  name: z.string().trim().min(2, 'Nome muito curto').max(120),
  description: z.string().trim().max(1000).optional(),
  categoryId: z.string().optional(),
  /** Vazio = oferecido em todas as unidades. */
  unitId: z.string().optional(),
  images: z.array(z.string().url('URL invalida')).max(5).default([]),
  price: z.coerce.number().nonnegative('Preco invalido'),
  durationMinutes: z.coerce
    .number()
    .int()
    .positive('Duracao invalida')
    .max(24 * 60),
  maxDiscountPercent: percentSchema.default(0),
  autoDiscountPercent: percentSchema.default(0),
  isActive: z.boolean().default(true),
});

export const updateServiceSchema = serviceInputSchema.partial();

// ---------------------------------------------------------------------------
// Agendamento e atendimento (blocos 3 e 4)
// ---------------------------------------------------------------------------

export const appointmentStatusSchema = z.enum(APPOINTMENT_STATUSES);

/** Um servico da comanda; o desconto e validado contra o teto do servico. */
export const appointmentItemSchema = z.object({
  serviceId: idSchema,
  discountPercent: percentSchema.default(0),
  /** Cada servico tem seu profissional. Nulo = ainda nao definido. */
  professionalId: z.string().optional(),
  /** Sem hora propria, o servico entra na sequencia do anterior. */
  startAt: z.coerce.date().optional(),
});

export const appointmentInputSchema = z.object({
  customerId: idSchema,
  unitId: idSchema,
  professionalId: z.string().optional(),
  /** "Tanto faz quem atende": o sistema escolhe quem estiver livre. */
  anyProfessional: z.boolean().default(false),
  startAt: z.coerce.date(),
  /** A comanda: pelo menos um servico. */
  items: z.array(appointmentItemSchema).min(1, 'Escolha ao menos um servico'),
  notes: z.string().trim().max(2000).optional(),
  /** Cashback do cliente usado como desconto; o saldo limita no encerramento. */
  cashbackUsed: z.coerce.number().nonnegative().max(99_999_999).default(0),
  /** Cupom aplicado na compra; o desconto vem calculado por /coupons/apply. */
  couponId: z.string().optional(),
  couponCode: z.string().optional(),
  couponDiscount: z.coerce.number().nonnegative().max(99_999_999).default(0),
});

export const updateAppointmentSchema = appointmentInputSchema.partial();

/** Horarios livres de um profissional em um intervalo. */
export const availabilityQuerySchema = z
  .object({
    /** Vazio quando a pergunta e "quem estiver livre". */
    professionalId: z.string().optional(),
    /**
     * Uniao dos horarios de todos os profissionais da unidade. Aceita texto
     * porque a disponibilidade e consultada por querystring, onde tudo chega
     * como string - e `coerce.boolean` transformaria "false" em true.
     */
    anyProfessional: z
      .union([z.boolean(), z.enum(['true', 'false'])])
      .default(false)
      .transform((valor) => valor === true || valor === 'true'),
    unitId: z.string().optional(),
    from: z.coerce.date(),
    to: z.coerce.date(),
    durationMinutes: z.coerce.number().int().min(5).max(600).default(30),
    /** Agendamento em edicao: os itens dele nao contam como ocupado. */
    ignoreId: z.string().optional(),
  })
  .refine((query) => query.anyProfessional || Boolean(query.professionalId), {
    message: 'Escolha o profissional ou peca qualquer um',
    path: ['professionalId'],
  });

export const cancelAppointmentSchema = z.object({
  reason: z.string().trim().max(240).optional(),
});

/** Anotacoes do atendimento (bloco 4). */
export const appointmentNotesSchema = z.object({
  notes: z.string().trim().max(2000),
});

// ---------------------------------------------------------------------------
// Recebiveis
// ---------------------------------------------------------------------------

export const paymentMethodSchema = z.enum(PAYMENT_METHODS);
export const receivableStatusSchema = z.enum(RECEIVABLE_STATUSES);

/** A maquininha do salao: ele so escolhe a adquirente e, se quiser, um apelido. */
export const paymentMachineInputSchema = z.object({
  acquirerId: idSchema,
  nickname: z.string().trim().max(60).optional(),
  isActive: z.boolean().default(true),
});

export const updatePaymentMachineSchema = paymentMachineInputSchema.partial();

/** Taxa e prazo de uma forma; `installments` so importa no credito. */
export const machineRuleInputSchema = z.object({
  method: paymentMethodSchema,
  installments: z.coerce.number().int().min(1).max(MAX_INSTALLMENTS).default(1),
  feePercent: z.coerce.number().min(0, 'Taxa invalida').max(99.99),
  daysToReceive: z.coerce.number().int().min(0).max(365),
});

/** Edicao em lote: manda a tabela inteira da maquininha de uma vez. */
export const machineRulesInputSchema = z.object({
  rules: z.array(machineRuleInputSchema).max(3 + MAX_INSTALLMENTS),
});

/**
 * Uma forma de pagamento da venda. Pagamento misto e mandar mais de uma; a
 * soma tem de fechar com o total do atendimento.
 */
export const paymentInputSchema = z
  .object({
    method: paymentMethodSchema,
    machineId: z.string().optional(),
    installments: z.coerce.number().int().min(1).max(MAX_INSTALLMENTS).default(1),
    amount: z.coerce.number().positive('Informe um valor maior que zero').max(99_999_999),
  })
  .refine((payment) => payment.method === 'CASH' || Boolean(payment.machineId), {
    message: 'Escolha a maquininha',
    path: ['machineId'],
  });

export const settlePaymentsSchema = z.object({
  payments: z.array(paymentInputSchema).min(1, 'Informe como o cliente pagou'),
});

/** Baixa do recebivel; sem valor informado, vale o liquido previsto. */
export const receiveReceivableSchema = z.object({
  receivedAt: z.coerce.date().optional(),
  receivedAmount: z.coerce.number().nonnegative().max(99_999_999).optional(),
  note: z.string().trim().max(240).optional(),
});

/** Antecipacao: o dinheiro vem antes e custa um pouco mais. */
export const anticipateReceivableSchema = z.object({
  dueDate: z.coerce.date(),
  anticipationFee: z.coerce.number().nonnegative().max(99_999_999).default(0),
});

// ---------------------------------------------------------------------------
// Comissoes
// ---------------------------------------------------------------------------

export const commissionRuleInputSchema = z.object({
  /** Vazio = vale para todos os profissionais. */
  professionalId: z.string().optional(),
  /** Vazio = vale para todos os servicos. */
  serviceId: z.string().optional(),
  percent: z.coerce.number().min(0, 'Percentual invalido').max(100),
  isActive: z.boolean().default(true),
});

export const updateCommissionRuleSchema = commissionRuleInputSchema.partial();

// ---------------------------------------------------------------------------
// Cupons
// ---------------------------------------------------------------------------

export const couponTypeSchema = z.enum(COUPON_TYPES);

/** A forma crua do cupom; as duas checagens abaixo montam o schema de entrada. */
const couponBaseSchema = z.object({
  /** Codigo em caixa alta: e assim que o cliente le e digita. */
  code: z
    .string()
    .trim()
    .min(3, 'Codigo muito curto')
    .max(24)
    .transform((value) => value.toUpperCase()),
  description: z.string().trim().max(160).optional(),
  type: couponTypeSchema,
  value: z.coerce.number().positive('Informe o desconto').max(99_999_999),
  minAmount: z.coerce.number().nonnegative().max(99_999_999).optional(),
  maxDiscount: z.coerce.number().nonnegative().max(99_999_999).optional(),
  startsAt: z.coerce.date().optional(),
  endsAt: z.coerce.date().optional(),
  usageLimit: z.coerce.number().int().min(1).max(1_000_000).optional(),
  perCustomerLimit: z.coerce.number().int().min(1).max(1000).optional(),
  /** Vazio = vale para todos os servicos. */
  serviceIds: z.array(idSchema).max(50).default([]),
  isActive: z.boolean().default(true),
});

export const couponInputSchema = couponBaseSchema
  .refine((coupon) => coupon.type !== 'PERCENT' || coupon.value <= 100, {
    message: 'Percentual nao pode passar de 100',
    path: ['value'],
  })
  .refine((coupon) => !coupon.endsAt || !coupon.startsAt || coupon.endsAt >= coupon.startsAt, {
    message: 'O fim nao pode ser antes do inicio',
    path: ['endsAt'],
  });

export const updateCouponSchema = couponBaseSchema.partial();

/** Simulacao do cupom na compra, antes de gravar o atendimento. */
export const applyCouponSchema = z.object({
  code: z.string().trim().min(1, 'Informe o codigo'),
  amount: z.coerce.number().nonnegative(),
  customerId: z.string().optional(),
  serviceIds: z.array(idSchema).default([]),
});

// ---------------------------------------------------------------------------
// Cashback
// ---------------------------------------------------------------------------

/** Minuto do dia (0 a 1439); e assim que a faixa de horario da regra e guardada. */
const minuteOfDaySchema = z.coerce
  .number()
  .int()
  .min(0)
  .max(24 * 60 - 1);

export const cashbackRuleInputSchema = z
  .object({
    name: z.string().trim().min(2, 'Informe o nome').max(80),
    percent: z.coerce.number().int().min(1, 'O percentual precisa ser maior que zero').max(100),
    /** Vazio = vale para todos os servicos. */
    serviceId: z.string().optional(),
    /** Vazio = vale para todas as unidades. */
    unitId: z.string().optional(),
    /** Vazio = vale todos os dias. */
    weekdays: z.array(z.coerce.number().int().min(0).max(6)).max(7).default([]),
    startMinutes: minuteOfDaySchema.optional(),
    endMinutes: minuteOfDaySchema.optional(),
    validityDays: z.coerce.number().int().min(1).max(3650).default(90),
    minAmount: z.coerce.number().nonnegative().max(99_999_999).optional(),
    isActive: z.boolean().default(true),
  })
  .refine(
    (rule) =>
      rule.startMinutes === undefined ||
      rule.endMinutes === undefined ||
      rule.endMinutes > rule.startMinutes,
    { message: 'O fim do horario precisa ser depois do inicio', path: ['endMinutes'] },
  );

export const updateCashbackRuleSchema = cashbackRuleInputSchema.innerType().partial();

// ---------------------------------------------------------------------------
// Financeiro - contas a pagar e a receber
// ---------------------------------------------------------------------------

export const financeEntryTypeSchema = z.enum(FINANCE_ENTRY_TYPES);
export const financeEntryStatusSchema = z.enum(FINANCE_ENTRY_STATUSES);

export const financeCategoryInputSchema = z.object({
  name: z.string().trim().min(2, 'Nome muito curto').max(60),
  type: financeEntryTypeSchema,
  isActive: z.boolean().default(true),
});

export const updateFinanceCategorySchema = financeCategoryInputSchema.partial();

export const financeEntryInputSchema = z.object({
  type: financeEntryTypeSchema,
  description: z.string().trim().min(2, 'Descreva a conta').max(160),
  amount: z.coerce.number().positive('Informe um valor maior que zero').max(99_999_999),
  dueDate: z.coerce.date(),
  categoryId: z.string().optional(),
  /** Vazio = conta da empresa toda, sem unidade responsavel. */
  unitId: z.string().optional(),
  notes: z.string().trim().max(2000).optional(),
});

export const updateFinanceEntrySchema = financeEntryInputSchema.partial();

export const financeRecurrenceSchema = z.enum(FINANCE_RECURRENCES);

/** Cadastro da conta: e dele que saem os vencimentos previstos. */
export const financeBillInputSchema = z
  .object({
    type: financeEntryTypeSchema,
    recurrence: financeRecurrenceSchema.default('MONTHLY'),
    description: z.string().trim().min(2, 'Descreva a conta').max(160),
    amount: z.coerce.number().positive('Informe um valor maior que zero').max(99_999_999),
    /** Primeiro vencimento; nas recorrentes define tambem o dia. */
    startDate: z.coerce.date(),
    endDate: z.coerce.date().optional(),
    categoryId: z.string().optional(),
    unitId: z.string().optional(),
    notes: z.string().trim().max(2000).optional(),
    isActive: z.boolean().default(true),
  })
  .refine((bill) => !bill.endDate || bill.endDate >= bill.startDate, {
    message: 'O fim nao pode ser antes do primeiro vencimento',
    path: ['endDate'],
  });

export const updateFinanceBillSchema = financeBillInputSchema.innerType().partial();

/** Baixa da conta. Sem valor informado, vale o previsto. */
export const payFinanceEntrySchema = z.object({
  paidAt: z.coerce.date().optional(),
  paidAmount: z.coerce
    .number()
    .positive('Informe um valor maior que zero')
    .max(99_999_999)
    .optional(),
});

// ---------------------------------------------------------------------------
// Payloads de jobs (compartilhados entre API e worker)
// ---------------------------------------------------------------------------

export const welcomeEmailJobSchema = z.object({
  userId: idSchema,
  email: emailSchema,
  name: z.string().min(1),
});

export const passwordResetEmailJobSchema = z.object({
  userId: idSchema,
  email: emailSchema,
  name: z.string().min(1),
  resetUrl: z.string().url(),
});

export const periodReportJobSchema = z.object({
  requestedBy: idSchema,
  from: z.coerce.date(),
  to: z.coerce.date(),
});

/** O que o worker precisa para montar e enviar a mensagem do atendimento. */
export const appointmentMessageJobSchema = z.object({
  companyId: idSchema,
  appointmentId: idSchema,
  event: z.enum(MESSAGE_EVENTS),
});

/**
 * Uma mensagem ja gravada no historico esperando para sair. O worker so
 * precisa do id: o texto, o destino e a conversa ja estao na linha.
 */
export const sendMessageJobSchema = z.object({
  companyId: idSchema,
  messageId: idSchema,
});

/** Uma rodada de campanha para disparar. O publico e resolvido na hora. */
export const campaignRunJobSchema = z.object({
  companyId: idSchema,
  runId: idSchema,
});

/** A conferencia de mensagem sem resposta, 30s depois que ela chegou. */
export const unreadCheckJobSchema = z.object({
  companyId: idSchema,
  conversationId: idSchema,
  messageId: idSchema,
});

/** Uma mensagem do cliente ja gravada, esperando o funil e o assistente. */
export const inboundMessageJobSchema = z.object({
  companyId: idSchema,
  conversationId: idSchema,
  messageId: idSchema,
});

/** O aviso de cancelamento sai do proprio cancelamento. */
export const appointmentCanceledJobSchema = z.object({
  companyId: idSchema,
  appointmentId: idSchema,
});

export const dispatchWebhookJobSchema = z.object({
  url: z.string().url(),
  event: z.string().min(1),
  payload: z.record(z.unknown()).default({}),
});

export type WelcomeEmailJob = z.infer<typeof welcomeEmailJobSchema>;
export type PasswordResetEmailJob = z.infer<typeof passwordResetEmailJobSchema>;
export type PeriodReportJob = z.infer<typeof periodReportJobSchema>;
export type DispatchWebhookJob = z.infer<typeof dispatchWebhookJobSchema>;
export type AppointmentMessageJob = z.infer<typeof appointmentMessageJobSchema>;
export type SendMessageJob = z.infer<typeof sendMessageJobSchema>;
export type CampaignRunJob = z.infer<typeof campaignRunJobSchema>;
export type AppointmentCanceledJob = z.infer<typeof appointmentCanceledJobSchema>;
export type InboundMessageJob = z.infer<typeof inboundMessageJobSchema>;
export type UnreadCheckJob = z.infer<typeof unreadCheckJobSchema>;
export type MessageTemplateInput = z.infer<typeof messageTemplateInputSchema>;

// ---------------------------------------------------------------------------
// Tipos inferidos
// ---------------------------------------------------------------------------

export type PaginationQuery = z.infer<typeof paginationQuerySchema>;
export type PaginationMeta = z.infer<typeof paginationMetaSchema>;
export type CreateUserInput = z.infer<typeof createUserSchema>;
export type UpdateUserInput = z.infer<typeof updateUserSchema>;
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;
export type GroupInput = z.infer<typeof groupInputSchema>;
export type CompanyInput = z.infer<typeof companyInputSchema>;
export type UnitInput = z.infer<typeof unitInputSchema>;
export type CustomerInput = z.infer<typeof customerInputSchema>;
export type CategoryInput = z.infer<typeof categoryInputSchema>;
export type ServiceInput = z.infer<typeof serviceInputSchema>;
export type AppointmentInput = z.infer<typeof appointmentInputSchema>;
export type UpdateAppointmentInput = z.infer<typeof updateAppointmentSchema>;
export type AvailabilityQuery = z.infer<typeof availabilityQuerySchema>;
export type FinanceCategoryInput = z.infer<typeof financeCategoryInputSchema>;
export type FinanceEntryInput = z.infer<typeof financeEntryInputSchema>;
export type FinanceBillInput = z.infer<typeof financeBillInputSchema>;
export type CashbackRuleInput = z.infer<typeof cashbackRuleInputSchema>;
export type CouponInput = z.infer<typeof couponInputSchema>;
export type CommissionRuleInput = z.infer<typeof commissionRuleInputSchema>;
export type ApplyCouponInput = z.infer<typeof applyCouponSchema>;
export type PaymentMachineInput = z.infer<typeof paymentMachineInputSchema>;
export type MachineRuleInput = z.infer<typeof machineRuleInputSchema>;
export type PaymentInput = z.infer<typeof paymentInputSchema>;
export type ReceiveReceivableInput = z.infer<typeof receiveReceivableSchema>;
export type AnticipateReceivableInput = z.infer<typeof anticipateReceivableSchema>;
export type PayFinanceEntryInput = z.infer<typeof payFinanceEntrySchema>;

// ---------------------------------------------------------------------------
// Mensagens (WhatsApp)
// ---------------------------------------------------------------------------

export const messageTemplateInputSchema = z.object({
  name: z.string().trim().min(2, 'Nome muito curto').max(80),
  event: z.enum(MESSAGE_EVENTS),
  body: z.string().trim().min(5, 'Escreva a mensagem').max(1000),
  /** Template aprovado no Twilio; sem ele so vale a janela de 24h. */
  contentSid: z.string().trim().max(64).optional(),
  isActive: z.boolean().default(true),
});

export const updateMessageTemplateSchema = messageTemplateInputSchema.partial();
